import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

export const hasSupabaseConfig = Boolean(url && anonKey)

/**
 * 환경변수가 없으면 null. 이때 앱은 LocalRepository(시드 데이터)로 동작한다.
 * service_role 키는 클라이언트에 절대 넣지 않는다 — Edge Function 전용.
 */
export const supabase: SupabaseClient<Database> | null = hasSupabaseConfig
  ? createClient<Database>(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null

/**
 * 진행 중인 세션 확보 요청. 동시에 여러 번 불려도 로그인은 한 번만 나가게 한다.
 *
 * AppProvider 가 마운트되면서 getProfile() 과 listSavedWords() 를 동시에 부르는데,
 * 둘 다 세션이 없다고 판단해 각자 signInAnonymously() 를 호출하면 **익명 계정이 두 개**
 * 만들어진다. 그러면 온보딩 결과가 한쪽에만 저장되고, 다음에 열 때 다른 쪽으로 붙어
 * 진단 결과가 사라진 것처럼 보인다. 실제로 그런 계정 쌍이 여럿 쌓여 있었다.
 */
let pendingSession: Promise<string | null> | null = null

/** MVP 인증: 익명 로그인. 세션이 없으면 익명 사용자로 만든다. */
export function ensureSession(client: SupabaseClient<Database>): Promise<string | null> {
  if (pendingSession) return pendingSession
  pendingSession = resolveSession(client)
  // 성패와 무관하게 비워, 다음 호출이 다시 확인할 수 있게 한다.
  void pendingSession.finally(() => {
    pendingSession = null
  })
  return pendingSession
}

async function resolveSession(client: SupabaseClient<Database>): Promise<string | null> {
  const { data } = await client.auth.getSession()
  if (data.session) {
    // getSession() 은 로컬 캐시만 본다. 유저가 삭제돼도 JWT 는 만료 전까지 유효해서
    // (Supabase 는 30일 지난 익명 유저를 정리한다) 그대로 쓰면 쓰기마다 FK 위반이 난다.
    // 서버에 한 번 확인하고, 죽은 세션이면 버리고 새로 만든다.
    const { data: me, error } = await client.auth.getUser()
    if (!error && me.user) return me.user.id
    await client.auth.signOut({ scope: 'local' })
  }

  const { data: signed, error } = await client.auth.signInAnonymously()
  if (error) {
    console.warn('[supabase] anonymous sign-in failed:', error.message)
    return null
  }
  return signed.user?.id ?? null
}
